import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Card, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { announcementAPI } from '../api/announcementAPI';

function ToggleSwitch({ checked, onChange, label }) {
    return (
        <button
            role="switch"
            aria-checked={checked}
            aria-label={label}
            onClick={onChange}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500/30 ${
                checked ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
            }`}
        >
            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                checked ? 'translate-x-6' : 'translate-x-1'
            }`} />
        </button>
    );
}

export function Announcements() {
    const { isAuthenticated } = useAuth();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchAnnouncements = useCallback(async () => {
        setLoading(true);
        try {
            const res = await announcementAPI.getAll();
            setAnnouncements(res.data.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load announcements');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) fetchAnnouncements();
    }, [isAuthenticated, fetchAnnouncements]);

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setTitle('');
        setBody('');
    };

    const handleEdit = (a) => {
        setEditingId(a.id);
        setTitle(a.title);
        setBody(a.body);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSave = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            if (editingId) {
                await announcementAPI.update(editingId, { title: title.trim(), body: body.trim() });
                setSuccess('Announcement updated');
            } else {
                await announcementAPI.create({ title: title.trim(), body: body.trim() });
                setSuccess('Announcement created');
            }
            resetForm();
            fetchAnnouncements();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save announcement');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (a) => {
        try {
            await announcementAPI.update(a.id, { active: !a.active });
            fetchAnnouncements();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to toggle announcement');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this announcement? This cannot be undone.')) return;
        try {
            await announcementAPI.delete(id);
            setSuccess('Announcement deleted');
            fetchAnnouncements();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete announcement');
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const activeCount = announcements.filter((a) => a.active).length;

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Announcements</h1>
                    {!loading && announcements.length > 0 && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                            {activeCount} active of {announcements.length} total
                        </p>
                    )}
                </div>
                {!showForm && (
                    <Button onClick={() => { resetForm(); setShowForm(true); }} size="sm">
                        + New
                    </Button>
                )}
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            {showForm && (
                <Card className="mb-4 border-l-4 border-l-accent-500">
                    <CardBody>
                        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                            {editingId ? 'Edit Announcement' : 'New Announcement'}
                        </h2>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g. Service time change this Sunday"
                                    className="input-field w-full"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    Details <span className="text-slate-400 font-normal">(optional)</span>
                                </label>
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Add more context or instructions..."
                                    rows={3}
                                    className="input-field w-full"
                                />
                            </div>
                            <div className="flex gap-2 pt-1">
                                <Button onClick={handleSave} disabled={saving || !title.trim()} size="sm">
                                    {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Publish'}
                                </Button>
                                <Button variant="secondary" onClick={resetForm} size="sm">Cancel</Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            )}

            {loading ? (
                <p className="text-center text-slate-500 py-8">Loading announcements...</p>
            ) : announcements.length === 0 ? (
                <div className="text-center py-16">
                    <svg className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0" />
                    </svg>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">No announcements yet</p>
                    <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Create one to broadcast a message to all leaders.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {announcements.map((a) => (
                        <Card key={a.id} className={!a.active ? 'opacity-60' : ''}>
                            <CardBody>
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">{a.title}</h3>
                                            {!a.active && (
                                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                                                    Hidden
                                                </span>
                                            )}
                                        </div>
                                        {a.body && (
                                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2 whitespace-pre-wrap">{a.body}</p>
                                        )}
                                        <p className="text-xs text-slate-400 dark:text-slate-500">
                                            {a.author_name && <>{a.author_name} &middot; </>}{formatDate(a.created_at)}
                                        </p>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-3 flex-shrink-0 pt-0.5">
                                        <ToggleSwitch
                                            checked={a.active}
                                            onChange={() => handleToggle(a)}
                                            label={a.active ? 'Deactivate' : 'Activate'}
                                        />
                                        <button
                                            onClick={() => handleEdit(a)}
                                            className="p-1.5 rounded-lg text-slate-400 hover:text-accent-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                            title="Edit"
                                        >
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(a.id)}
                                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            title="Delete"
                                        >
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Announcements;
